// TipManagement Types

export interface User {
  id: string;
  name: string;
  rate: number;
  attendance: { [date: string]: boolean };
}

export interface Group {
  id: string;
  name: string;
  rate: number;
  users: User[];
  attendance: { [date: string]: boolean };
  isRealGroup?: boolean; // true for actual groups, false/undefined for standalone users
}

export interface RowItem {
  type: 'user' | 'group';
  id: string;
  name: string;
  rate: number;
  level: number;
  groupId?: string;
  isRealGroup?: boolean; // Propagate this to row items
}

export interface DayTotals {
  [date: string]: number;
}

// ============================================
// TIP DISTRIBUTION CONFIGURATION (Saveable)
// ============================================

// User configuration within the distribution
export interface TipDistributionUser {
  id: string;
  name: string;
  rate: number; // Percentage rate (0-100)
  attendance: { [date: string]: boolean }; // Attendance per day
}

// Group configuration within the distribution
export interface TipDistributionGroup {
  id: string;
  name: string;
  rate: number; // Group-level percentage rate
  isRealGroup: boolean; // true = actual group, false = standalone user
  users: TipDistributionUser[]; // Empty array for standalone users
  attendance: { [date: string]: boolean }; // Group attendance per day
}

// Daily tip data
export interface TipDistributionDay {
  date: string;
  declaredTotal: number; // User-declared tip amount for the day
  systemTotal: number; // Original system value (from API)
  distributedTotal: number; // Actual calculated distributed amount
}

// Complete tip distribution configuration object
export interface TipDistributionConfig {
  // Metadata
  id?: string; // ID when saved to backend
  createdAt?: string; // ISO timestamp when created
  updatedAt?: string; // ISO timestamp when last updated
  status: 'draft' | 'submitted' | 'approved' | 'rejected';
  
  // Date range
  startDate: string; // First date in distribution
  endDate: string; // Last date in distribution
  
  // Source data reference (optional, for traceability)
  shiftIds?: number[]; // Shift IDs used to generate this distribution
  sessionIds?: number[]; // Session IDs used
  
  // Groups and users configuration
  groups: TipDistributionGroup[];
  
  // Daily totals
  days: TipDistributionDay[];
  
  // Summary totals
  totals: {
    declared: number; // Sum of all declaredTotal
    system: number; // Sum of all systemTotal
    distributed: number; // Sum of all distributedTotal
    difference: number; // declared - system
  };
}

// Constants
export const RATE_OPTIONS = [0, 10, 15, 25, 35, 50, 75, 100];

// API Response Types
export interface UserStats {
  user_id?: number;
  user_name?: string;
  name?: string;
  total: number;
  tip: number;
  tip_as_opener?: number; // 100% of tip attributed to whoever opened the table (may be absent in older data)
  count: number;
  cancelled: number;
  cancelled_amount: number;
  avg_total: number;
  avg_cancelled: number;
  avg_tip_variation?: number;
  tip_total?: number;
  total_sales?: number;
  receipt_count?: number;
  avg_total_sales?: number;
}

export interface ShiftData {
  shift_id: number;
  name: string;
  start_time: string;
  end_time: string;
  active: boolean;
  open_user_id: number;
  open_user_name: string;
  close_user_id: number;
  close_user_name: string;
  gross_sales: number;
  net_sales: number;
  total_tip: number;
  users_count: number;
  valid_receipt_count: number;
  cancelled_receipt_count: number;
  users_stats: { [userId: string]: UserStats };
  type_stats: { [typeId: string]: any };
}

export interface SalesSession {
  sales_total_id: number;
  session_id: number;
  timestamp: string;
  start_timestamp: string;
  end_timestamp: string;
  day_number: number;
  total_charged: number;
  gross_sales: number;
  valid_receipt_count: number;
  users_count: number;
  net_sales: number;
  pending_payments_count: number;
  total_pending: number;
  gross_loss: number;
  net_loss: number;
  cancelled_receipt_count: number;
  avg_ticket: number;
  total_tip: number;
  avg_tip_by_ticket: number;
  total_discounts: number;
  total_coupons: number;
  avg_gross_sales: number;
  avg_net_sales: number;
  avg_net_loss: number;
  avg_total_tip: number;
  user_id: number;
  user_name: string;
  users_stats: { [userId: string]: UserStats };
  type_stats: { [typeId: string]: any };
  shifts: { [shiftId: string]: ShiftData } | null;
}

// TipData types for the distribution table
export interface TipUser {
  user_id: number;
  user_name: string;
  group?: string;
  rate: number;
  attended?: { [date: string]: boolean };
}

export interface DayTips {
  value: number;
  users: TipUser[];
}

export interface TipData {
  start_date: string;
  end_date: string;
  total_tips: number;
  total_distributed: number;
  total_employees: number;
  days: { [date: string]: DayTips };
}

// Historical tip record (saved distributions)
// Raw API response structure — data can be percentage or individual format
export interface TipRecordAPI {
  record_id: string;
  timestamp: string;
  created_at: string;
  local_id: number;
  user_id: string;
  user_name: string;
  cancelled: boolean;
  data: any; // Flexible: percentage format has `days`, individual format has `type: 'individual'`
}

// Transformed structure for UI
export type TipRecordType = 'percentage' | 'individual';

export interface TipRecord {
  id: string;
  created_at: string;
  start_date: string;
  end_date: string;
  total_tips: number;
  total_distributed: number;
  employee_count: number;
  shift_ids: number[];
  status: 'draft' | 'completed';
  recordType: TipRecordType;
  // Keep raw data for detail view
  rawData?: any;
}

// Helper function to transform API response to UI format
export function transformTipRecordFromAPI(apiRecord: TipRecordAPI): TipRecord {
  const data = apiRecord.data || {};
  const isIndividual = data.type === 'individual';
  
  return {
    id: apiRecord.record_id,
    created_at: apiRecord.created_at,
    start_date: data.start_date,
    end_date: data.end_date,
    total_tips: data.total_tips,
    total_distributed: data.total_distributed,
    employee_count: data.total_employees || data.total_employees || (data.users?.length ?? 0),
    shift_ids: isIndividual
      ? (data.shift_ids || [])
      : Object.keys(data.days || {}).map((_: string, i: number) => i),
    status: apiRecord.cancelled ? 'draft' : 'completed',
    recordType: isIndividual ? 'individual' : 'percentage',
    rawData: data,
  };
}

// ============================================
// DISTRIBUTION MODE
// ============================================

export type DistributionMode = 'percentage' | 'individual';

// ============================================
// INDIVIDUAL TIP DISTRIBUTION MODES
// ============================================

/**
 * Sub-modes for individual tip distribution:
 * - equal_split: Tips split equally among all waiters who served each table (uses `tip` field)
 * - table_opener: 100% of tip goes to whoever opened the table (uses `tip_as_opener` field)
 * - top_contributor: Tips distributed proportionally based on each waiter's sales volume
 */
export type IndividualDistributionMode = 'equal_split' | 'table_opener' | 'top_contributor';

// Payload when saving an individual distribution record
export interface IndividualSavePayload {
  type: 'individual';
  mode: IndividualDistributionMode;
  start_date: string;
  end_date: string;
  total_tips: number;
  total_distributed: number;
  total_employees: number;
  shift_ids: number[];
  users: Array<{
    user_id: number;
    user_name: string;
    total_tip: number;
    shift_count: number;
    avg_tip: number;
    share_percent: number;
  }>;
}

// ============================================
// INDIVIDUAL TIP DISTRIBUTION (new endpoint)
// ============================================

// Per-receipt tip attribution detail
export interface TipReceiptDetail {
  receipt_id: number;
  tip_share: number;
  total_tip: number;
  recipients_count: number;
  source: 'pedido_users' | 'fallback_user_id';
  table_opener?: { user_id: number; user_name: string };
}

// Per-user tip distribution from the new endpoint
export interface IndividualTipUser {
  user_id: number;
  user_name: string;
  total_tip: number;
  receipt_count: number;
  avg_tip: number;
  receipts: TipReceiptDetail[];
}

// Summary from the new endpoint
export interface TipDistributionSummary {
  total_tip: number;
  total_receipts_with_tip: number;
  total_receipts: number;
  session_count: number;
  sessions: number[];
}

// Full response from POST /GetTipDistribution/<local_id>
export interface TipDistributionResponse {
  users: { [userId: string]: IndividualTipUser };
  summary: TipDistributionSummary;
}

// Flattened shift for selection (extracted from sessions)
export interface SelectableShift {
  shift_id: number;
  session_id: number;
  name: string;
  start_time: string;
  end_time: string;
  total_tip: number;
  users_count: number;
  user_names: string[];
  gross_sales: number;
  net_sales: number;
  users_stats: { [userId: string]: UserStats };
}

// Helper function to extract shifts from sessions
export function extractShiftsFromSessions(sessions: SalesSession[]): SelectableShift[] {
  const shifts: SelectableShift[] = [];
  
  for (const session of sessions) {
    if (session.shifts) {
      for (const [shiftId, shift] of Object.entries(session.shifts)) {
        const userNames = Object.values(shift.users_stats)
          .map(u => u.name || u.user_name || 'Unknown')
          .filter(Boolean);
        
        shifts.push({
          shift_id: shift.shift_id,
          session_id: session.session_id,
          name: shift.name,
          start_time: shift.start_time,
          end_time: shift.end_time,
          total_tip: shift.total_tip,
          users_count: shift.users_count,
          user_names: userNames,
          gross_sales: shift.gross_sales,
          net_sales: shift.net_sales,
          users_stats: shift.users_stats,
        });
      }
    }
  }
  
  // Sort by start_time descending (most recent first)
  return shifts.sort((a, b) => 
    new Date(b.start_time).getTime() - new Date(a.start_time).getTime()
  );
}
